<template>
    <BasicFormControl
        v-bind="props"
        :id="id"
        :disabled="disabledOrHydrating"
        :has-error="hasError"
        :is-focused="isFocused"
        :error-message="computedError"
        :basic-error-message-id="basicErrorMessageId"
        @click:focus="isFocused = $event"
        @click:prepend-inner-icon="
            emits('click:prependInnerIcon', localModelValue)
        "
        @click:append-inner-icon="
            emits('click:appendInnerIcon', localModelValue)
        "
    >
        <template
            v-if="$slots.default"
            #default
        >
            <slot></slot>
        </template>
        <template
            v-if="$slots.prependInner"
            #prependInner
        >
            <slot name="prependInner"></slot>
        </template>

        <template
            v-if="$slots.appendInner"
            #appendInner
        >
            <slot name="appendInner"></slot>
        </template>
        <template #input>
            <component
                :is="tag"
                :id="id"
                :autocomplete="autocomplete"
                :name="name"
                :class="inputClasses"
                :placeholder="placeholder"
                :type="type"
                :value="localModelValue"
                :disabled="disabledOrHydrating"
                :required="required"
                :aria-describedby="basicErrorMessageId"
                :aria-invalid="hasError"
                :aria-label="ariaLabel"
                data-testid="input-value-div"
                @input="onInputListener"
                @blur="onBlur"
                @focus="isFocused = true"
                @keyup.enter="emits('keyup:enter', localModelValue)"
            >
            </component>
        </template>
    </BasicFormControl>
</template>

<script setup lang="ts">
import { type Size } from '@atoms/BasicFormControl/config';
import { type Size as BasicIconSize } from '@atoms/BasicIcon/sizes';
import { type Tag, type Type } from '@atoms/BasicInput/config';
import { useIsHydrating } from '@composables/useIsHydrating';

import BasicFormControl, {
    type BasicFormControlSlots,
} from '@atoms/BasicFormControl/BasicFormControl.vue';

import { useBasicErrorMessage } from '@composables/useBasicErrorMessage';
import {
    useInput,
    type InputEmits,
    type InputProps,
} from '@composables/useInput';
import {
    useValidation,
    type ValidateOnEvent,
} from '@composables/useValidation';

// TODO: extend with HTMLInputElement?
export interface BasicInputProps extends InputProps<string | number> {
    tag?: Tag;
    size?: Size;
    type?: Type;
    placeholder?: string;
    prependInnerIcon?: string;
    appendInnerIcon?: string;
    appendInnerIconSize?: BasicIconSize;
    prependInnerIconSize?: BasicIconSize;
    preserveActiveStyle?: boolean;
    validateOn?: ValidateOnEvent[];
    autocomplete?: string;
    superviseHydration?: boolean;
    ariaLabel?: string;
}

const props = withDefaults(defineProps<BasicInputProps>(), {
    id: '',
    label: '',
    modelValue: '',
    disabled: false,
    required: false,
    errorMessage: '',
    rules: () => [],
    tag: 'input',
    size: 'm',
    type: 'text',
    placeholder: '',
    prependInnerIcon: '',
    appendInnerIcon: '',
    superviseHydration: true,
    prependInnerIconSize: undefined,
    preserveActiveStyle: false,
    appendInnerIconSize: undefined,
    validateOn: () => ['input', 'blur'],
    name: '',
    autocomplete: 'on',
    ariaLabel: undefined,
});

export type BasicInputEmits = InputEmits<BasicInputProps['modelValue']> & {
    (e: 'blur', value: BasicInputProps['modelValue']): void;
    (e: 'click:appendInnerIcon', value?: BasicInputProps['modelValue']): void;
    (e: 'click:prependInnerIcon', value?: BasicInputProps['modelValue']): void;
    (e: 'keyup:enter', value?: BasicInputProps['modelValue']): void;
};

const emits = defineEmits<BasicInputEmits>();

export type BasicInputSlots = Omit<BasicFormControlSlots, 'input'>;

defineSlots<BasicInputSlots>();

const { isHydrating } = useIsHydrating();

const disabledOrHydrating = computed(() =>
    props.superviseHydration
        ? props.disabled || isHydrating.value
        : props.disabled
);

const { id } = useInput(
    'basic-input',
    toRef(() => props.id)
);

const { hasError, computedError, runValidationHandler } = useValidation(
    toRef(() => props.errorMessage),
    toRef(() => props.rules),
    toRef(() => props.modelValue),
    toRef(() => props.validateOn)
);
const { id: basicErrorMessageId } = useBasicErrorMessage(id, hasError);

const localModelValue = ref(props.modelValue || '');
const isFocused = ref(false);

const inputClasses = [
    'flex-1',
    'text-font-1',
    'placeholder:text-font-3',
    'placeholder:italic',
    'placeholder:font-normal',
    'outline-none',
    'fade',
    'bg-inherit',
    {
        'hover:cursor-not-allowed':
            disabledOrHydrating.value && props.preserveActiveStyle,
    },
];

const onInputListener = (e: Event) => {
    if (disabledOrHydrating.value) return;
    const { value } = e.target as HTMLInputElement;
    onInput(value);
};

const onInput = (givenValue: BasicInputProps['modelValue']) => {
    if (disabledOrHydrating.value) return;
    const value = givenValue || '';
    emits('update:modelValue', value);
    localModelValue.value = value;
    runValidationHandler(value, 'input');
};

const onBlur = () => {
    if (disabledOrHydrating.value) return;
    isFocused.value = false;
    emits('blur', localModelValue.value);
    runValidationHandler(localModelValue.value, 'blur');
};

watch(
    () => props.modelValue,
    (newValue) => {
        if (localModelValue.value !== newValue) {
            onInput(newValue);
        }
    }
);
</script>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
}

input[type='number'] {
    appearance: textfield;
}
</style>
